import { useCallback, useEffect, useMemo, useState } from "react";
import { PicoBubble, usePicoToast } from "../../../components/PicoBubble";
import { canSpeak, speak } from "../../../lib/speak";
import { useCourseApi } from "../../../lib/api/courseApi";
import { useDensity } from "../../../store/density";
import { hasMessage, useI18n } from "../../../i18n";
import { track } from "../../../lib/telemetry/telemetry";
import { picoReaction, reactionText, readPicoCatalog } from "../lib/reactions";
import type { PicoEvent } from "../lib/reactions";
import type { PicoShown, PicoReaction, PicoTable } from "../types";

/**
 * 피코 반응(SR-ACH-03) — 카탈로그 규칙 표를 세션(코스)마다 한 번 받아 두고, 이벤트가 나면
 *  - 문구를 고르고(카탈로그 → 셸 대체 키),
 *  - 토스트(FS-C1 피코 토스트 — 3초)로 보이며, SE 는 읽어 준다(자동 — SR-ACH-03 '읽어주기 동반'),
 *  - 계측 `pico`(이벤트 키 · 규칙 키)를 남긴다.
 * 화면 안 말풍선으로 보일 때(결과 창 · 올리기 완료 · 휴식 창)는 {@link PicoReactionBubble} 을 쓰고 react(…, {toast:false}).
 */
const tables = new Map<string, Promise<PicoTable>>();

/** 카탈로그 표(코스별 캐시). 못 받으면 빈 표 — 로컬 표로 대신한다 */
export function usePicoTable(): PicoTable | null {
  const api = useCourseApi();
  const [table, setTable] = useState<PicoTable | null>(null);
  useEffect(() => {
    if (!api) return;
    let alive = true;
    const key = "pico_reaction";
    let p = tables.get(key);
    if (!p) {
      p = api.catalog("pico_reaction").then(readPicoCatalog, () => new Map());
      tables.set(key, p);
      // 실패(빈 표)는 다음 화면에서 다시 받아 본다
      void p.then((m) => m.size === 0 && tables.delete(key));
    }
    void p.then((m) => alive && setTable(m));
    return () => {
      alive = false;
    };
  }, [api]);
  return table;
}

/** 시험용 — 캐시 비우기 */
export function resetPicoTableCache() {
  tables.clear();
}

export function usePico() {
  const table = usePicoTable();
  const { t, locale } = useI18n();
  const { density } = useDensity();
  const toast = usePicoToast();

  const resolve = useCallback(
    (event: PicoEvent): PicoShown => {
      const r = picoReaction(event, table);
      const has = (k: string) => hasMessage(k, locale);
      return { ...r, shownText: reactionText(t, locale, density, r, has) };
    },
    [table, t, locale, density],
  );

  const react = useCallback(
    (
      event: PicoEvent,
      opts: { key?: string; toast?: boolean; speak?: boolean } = {},
    ): PicoShown => {
      const r = resolve(event);
      track({ t: "pico", code: event, detail: r.source });
      if (opts.toast !== false) {
        toast.show(opts.key ?? `pico-${event}-${Date.now()}`, r.shownText);
        if (density === "se" && opts.speak !== false && canSpeak())
          speak(r.shownText, locale === "ko" ? "ko-KR" : "en-US");
      }
      return r;
    },
    [resolve, toast, density, locale],
  );

  return useMemo(() => ({ table, resolve, react }), [table, resolve, react]);
}

/** 화면 안 피코 반응 말풍선 — 반응 표정·애니메이션 + 문구(SE 읽어 주기 버튼) */
export function PicoReactionBubble({
  reaction,
  text,
  live = true,
}: {
  reaction: PicoReaction;
  /** 문구를 바꿔 보일 때(예: 결과 창의 힌트) — 없으면 반응 문구 */
  text?: string;
  live?: boolean;
}) {
  const shown = (reaction as Partial<PicoShown>).shownText ?? "";
  return (
    <div
      className={`pf-pico-react pf-pico-react--${reaction.animation}`}
      data-pico-event={reaction.event}
      data-pico-source={reaction.source}
    >
      <PicoBubble
        text={text ?? shown}
        avatar={reaction.avatar}
        cheer={reaction.animation === "cheer"}
        live={live}
      />
    </div>
  );
}
